import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/toc.default.js';
import * as wireframeSkin from './skins/toc.wireframe.js';

export const TOC_MODES = Object.freeze(['auto', 'list', 'dropdown']);
export const TOC_AUTO_BREAKPOINT = 768;

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunToc extends RunElement {
  #mode = 'auto';
  #activeAnchor = '';
  #sections = [];
  #resolvedMode = 'list';
  #intersectionObserver = null;
  #resizeObserver = null;
  #rendering = false;

  static get observedAttributes() {
    return ['mode', 'active-anchor'];
  }

  _onSectionRegisterBound = (e) => this.#handleSectionRegister(e);
  _onSectionUnregisterBound = (e) => this.#handleSectionUnregister(e);
  _onShadowClickBound = (e) => this.#handleShadowClick(e);
  _onShadowChangeBound = (e) => this.#handleShadowChange(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'navigation');

    // vi-08: listen at document level so unregister events from
    // disconnectedCallback on run-section elements are caught.
    document.addEventListener('run-section-register', this._onSectionRegisterBound);
    document.addEventListener('run-section-unregister', this._onSectionUnregisterBound);

    // Shadow-root event delegation for skin-rendered run-link and run-select children.
    this.shadowRoot.addEventListener('click', this._onShadowClickBound);
    this.shadowRoot.addEventListener('run-select-change', this._onShadowChangeBound);
    this.shadowRoot.addEventListener('change', this._onShadowChangeBound);

    this.#setupResizeObserver();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('run-section-register', this._onSectionRegisterBound);
    document.removeEventListener('run-section-unregister', this._onSectionUnregisterBound);

    this.shadowRoot.removeEventListener('click', this._onShadowClickBound);
    this.shadowRoot.removeEventListener('run-select-change', this._onShadowChangeBound);
    this.shadowRoot.removeEventListener('change', this._onShadowChangeBound);

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'mode':          this.mode         = newValue ?? 'auto'; break;
      case 'active-anchor': this.activeAnchor = newValue ?? '';     break;
    }
  }

  get mode() { return this.#mode; }
  set mode(value) {
    const valid = TOC_MODES;
    const str = String(value ?? '').trim();
    const coerced = valid.includes(str) ? str : 'auto';
    if (this.#mode === coerced) return;
    this.#mode = coerced;
    this.setAttribute('mode', coerced);
    if (coerced === 'auto') {
      this.#handleResize();
    } else {
      this.#resolvedMode = coerced;
    }
    this.#updateAria();
    this.requestUpdate();
  }

  get activeAnchor() { return this.#activeAnchor; }
  set activeAnchor(value) {
    const coerced = String(value ?? '').trim();
    if (this.#activeAnchor === coerced) return;
    this.#activeAnchor = coerced;
    if (coerced === '') {
      this.removeAttribute('active-anchor');
    } else {
      this.setAttribute('active-anchor', coerced);
    }
    this.#updateAria();
    this.requestUpdate();
  }

  // Consumer-facing getter.
  get entries() {
    return this.#sections.map(({ anchor, heading, level }) => ({ anchor, heading, level }));
  }

  // Skin-facing read-only accessors — not part of the public consumer API.
  get sections() {
    return this.#sections.map(({ anchor, heading, level }) => ({ anchor, heading, level }));
  }

  get resolvedMode() {
    return this.#resolvedMode;
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    this.setAttribute('aria-label', 'Table of contents');
  }

  #setupIntersectionObserver() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
    if (this.#sections.length === 0) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => this.#handleIntersection(entries),
      { threshold: 0 }
    );
    this.#sections.forEach(section => {
      if (section.element) {
        this.#intersectionObserver.observe(section.element);
      }
    });
  }

  #setupResizeObserver() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    this.#resizeObserver = new ResizeObserver(() => this.#handleResize());
    this.#resizeObserver.observe(this);
  }

  #handleResize() {
    if (this.#mode !== 'auto') return;
    const raw = getComputedStyle(this).getPropertyValue('--run-toc-breakpoint-width').trim();
    const breakpoint = parseFloat(raw) || TOC_AUTO_BREAKPOINT;
    const newMode = this.offsetWidth < breakpoint ? 'dropdown' : 'list';
    if (newMode !== this.#resolvedMode) {
      this.#resolvedMode = newMode;
      this.requestUpdate();
    }
  }

  // vi-07: topmost intersecting element (closest to top of viewport) becomes active.
  // When no section is intersecting, active-anchor retains its last known value.
  #handleIntersection(entries) {
    entries.forEach(entry => {
      const section = this.#sections.find(s => s.element === entry.target);
      if (section) {
        section.intersecting = entry.isIntersecting && entry.intersectionRatio > 0;
        section.top = entry.boundingClientRect.top;
      }
    });

    const intersecting = this.#sections.filter(s => s.intersecting);
    if (intersecting.length === 0) return;

    const topmost = intersecting.reduce((a, b) => a.top < b.top ? a : b);
    this.activeAnchor = topmost.anchor;
  }

  // vi-01: newly registered section appended at end (DOM order preserved).
  #handleSectionRegister(event) {
    const { anchor, heading, level, element } = event.detail ?? {};
    if (!anchor) return;
    if (this.#sections.find(s => s.anchor === anchor)) return;
    this.#sections.push({
      anchor,
      heading: heading ?? '',
      level: level ?? 2,
      element: element ?? null,
      intersecting: false,
      top: 0,
    });
    this.#setupIntersectionObserver();
    this.requestUpdate();
  }

  // vi-01: unregistered sections removed immediately.
  #handleSectionUnregister(event) {
    const { anchor } = event.detail ?? {};
    if (!anchor) return;
    const idx = this.#sections.findIndex(s => s.anchor === anchor);
    if (idx === -1) return;
    this.#sections.splice(idx, 1);
    this.#setupIntersectionObserver();
    if (this.#activeAnchor === anchor) {
      this.activeAnchor = '';
    }
    this.requestUpdate();
  }

  // vi-06: intercept run-link clicks in shadow DOM.
  #handleShadowClick(event) {
    const path = event.composedPath();
    const link = path.find(el => el && el.localName === 'run-link');
    if (!link) return;
    const href = (link.getAttribute('href') ?? '').trim();
    if (!href.startsWith('#')) return;
    const anchor = href.slice(1);
    if (!anchor) return;
    event.preventDefault();
    this.#navigateTo(anchor);
  }

  // vi-05: intercept run-select change in shadow DOM.
  #handleShadowChange(event) {
    const path = event.composedPath();
    const target = path[0];
    if (!target || target.localName !== 'run-select') return;
    const anchor = (event.detail?.value ?? target.value ?? '').trim();
    if (!anchor) return;
    this.#navigateTo(anchor);
  }

  #navigateTo(anchor) {
    const element = this.#sections.find(s => s.anchor === anchor)?.element ?? null;
    const event = this.#emitNavigate(anchor, element);
    if (event.defaultPrevented) return;
    const target = document.getElementById(anchor);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }

  #emitNavigate(anchor, element) {
    const event = new CustomEvent('run-toc-navigate', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { anchor, element },
    });
    this.dispatchEvent(event);
    return event;
  }
}

if (!customElements.get('run-toc')) {
  customElements.define('run-toc', RunToc);
}

export { RunToc };
export default RunToc;